import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListStockLocations,
  useGetStockLocationBalances,
  useCreateStockLocationTransfer,
  getListStockLocationTransfersQueryKey,
  getGetStockLocationsUnplacedQueryKey,
  getGetStockLocationBalancesQueryKey,
  getListInventoryItemsQueryKey,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ItemCombobox } from "./item-combobox";

// Phase 58 Plan 13 (LOC-01/02) — location-to-location transfer form. Item,
// from-location (showing its available quantity), to-location, quantity,
// optional note. Submit is disabled client-side once quantity exceeds the
// source balance, and the server's insufficient_location_stock 409 is
// surfaced verbatim if it still occurs (a stale balance read, a race with
// another transfer, etc.).

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill from a CTA elsewhere on the Locations tab (e.g. "Move out of Unassigned"). */
  initialItemId?: number | null;
  initialFromLocationId?: number | null;
}

export function StockLocationTransferDialog({
  open,
  onOpenChange,
  initialItemId = null,
  initialFromLocationId = null,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [itemId, setItemId] = useState<number | null>(initialItemId);
  const [fromLocationId, setFromLocationId] = useState<number | null>(initialFromLocationId);
  const [toLocationId, setToLocationId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");

  // This dialog is mounted once by the parent and re-opened with different
  // pre-fills from different CTAs (per-item "Transfer", unplaced-report
  // "Move out of Unassigned", the plain "New transfer" button) — reseed on
  // every open rather than only on mount.
  useEffect(() => {
    if (open) {
      setItemId(initialItemId);
      setFromLocationId(initialFromLocationId);
      setToLocationId(null);
      setQuantity("");
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItemId, initialFromLocationId]);

  const { data: locations = [] } = useListStockLocations();
  const activeLocations = useMemo(() => locations.filter((l) => l.isActive), [locations]);

  const balancesQuery = useGetStockLocationBalances(
    { itemId: itemId ?? 0 },
    {
      query: {
        enabled: itemId != null,
        queryKey: getGetStockLocationBalancesQueryKey({ itemId: itemId ?? 0 }),
      },
    },
  );
  const fromBalance =
    balancesQuery.data?.locations.find((l) => l.locationId === fromLocationId)?.quantityOnHand ?? null;

  const qtyNum = Number(quantity);
  const qtyValid = quantity.trim() !== "" && Number.isFinite(qtyNum) && qtyNum > 0;
  const exceedsBalance = fromBalance != null && qtyValid && qtyNum > fromBalance;
  const canSubmit =
    itemId != null &&
    fromLocationId != null &&
    toLocationId != null &&
    fromLocationId !== toLocationId &&
    qtyValid &&
    !exceedsBalance;

  const createMut = useCreateStockLocationTransfer({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListStockLocationTransfersQueryKey() });
        qc.invalidateQueries({ queryKey: getGetStockLocationsUnplacedQueryKey() });
        if (itemId != null) {
          qc.invalidateQueries({ queryKey: getGetStockLocationBalancesQueryKey({ itemId }) });
        }
        qc.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
        toast({ title: "Transfer recorded." });
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const anyErr = err as { data?: { code?: string; message?: string } } | undefined;
        toast({
          title:
            anyErr?.data?.code === "insufficient_location_stock"
              ? "Not enough stock at the source location"
              : "Couldn't record the transfer.",
          description: anyErr?.data?.message,
          variant: "destructive",
        });
      },
    },
  });

  function onSubmit() {
    if (!canSubmit || itemId == null || fromLocationId == null || toLocationId == null) return;
    createMut.mutate({
      data: { itemId, fromLocationId, toLocationId, quantity: qtyNum, note: note.trim() || null },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer stock</DialogTitle>
          <DialogDescription>
            Move quantity from one location to another for a single item. The item's total stays the same.
          </DialogDescription>
        </DialogHeader>

        {/* Plain markup, not FormField/FormLabel — this dialog has no react-hook-form
            context and FormLabel throws without a FormField ancestor. */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Item</label>
            <ItemCombobox
              value={itemId}
              onChange={(id) => {
                setItemId(id);
                setFromLocationId(null);
              }}
              placeholder="Pick an item"
              showTier={false}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">From</label>
              <Select
                value={fromLocationId != null ? String(fromLocationId) : ""}
                onValueChange={(v) => setFromLocationId(Number(v))}
                disabled={itemId == null}
              >
                <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  {(balancesQuery.data?.locations ?? []).map((l) => (
                    <SelectItem key={l.locationId} value={String(l.locationId)}>
                      {l.locationName} · {l.quantityOnHand} on hand
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">To</label>
              <Select
                value={toLocationId != null ? String(toLocationId) : ""}
                onValueChange={(v) => setToLocationId(Number(v))}
              >
                <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent>
                  {activeLocations
                    .filter((l) => l.id !== fromLocationId)
                    .map((l) => (
                      <SelectItem key={l.id} value={String(l.id)}>
                        {l.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Quantity</label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {fromBalance != null && (
              <p className={cn("text-xs", exceedsBalance ? "text-destructive" : "text-muted-foreground")}>
                {fromBalance} available at the source location
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Note</label>
            <Textarea placeholder="Optional" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit || createMut.isPending}>
            {createMut.isPending ? "Transferring…" : "Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
